import { Box, Checkbox, IconButton, TableCell, Tooltip } from '@mui/material';
import { styled } from '@mui/material/styles';
import { Check2Circle } from '@styled-icons/bootstrap';
import { PartnerPlaceholderImage } from 'assets/img';
import DocumentIntroTitle from 'FeatureModules/Document/Components/DocumentIntroTitle';
import { StyledTableBody, StyledTableRow } from 'Components/TableBase/Table';
import includes from 'lodash/includes';
import moment from 'moment';
import React, { FC, MouseEvent, ReactNode } from 'react';
import Moment from 'react-moment';
import { Booleanish } from 'types/booleanish.types';
import { IBaseDocument, IDocument } from 'FeatureModules/Document/types/document.types';
import { IHeaderColumn } from 'Components/TableBase/SortableTableHead';
import { EDocumentsTableColumnKeys } from '../../utils/documentHeaderColumns';
import { ThemeTypography } from 'DesignSystem';

export interface IDocumentTableBodyProps {
  documents: IDocument[];
  currentPage: number;
  rowsPerPage: number;
  showPagination: boolean;
  actions?: IDocumentTableBodyAction[];
  selectedIds?: string[];
  disableSelection?: boolean;
  displayOwner?: boolean;
  headerColumns: IHeaderColumn[];
  'data-testid': string;
  handleClickDocument?: (document: IBaseDocument) => void;
  onSelect: (id: string) => void;
}

export interface IDocumentTableBodyAction {
  tooltip: string;
  icon: ReactNode;
  disabledTooltip?: string;
  disableIfDocumentUsed?: boolean;
  onClick: (document: IBaseDocument) => void;
  dataTestId?: string;
}

const UsageIcon = styled(Check2Circle)(({ theme }) => ({
  marginRight: theme.spacing(1),
}));

const StyledIconButton = styled(IconButton)(({ theme }) => ({
  padding: theme.spacing(0, 4, 0, 0),
  color: theme.palette.text.secondary,
}));

const ExpiredDate = styled('div')(({ theme }) => ({
  color: theme.palette.error.main,
}));

const StyledImage = styled('div')<{ backgroundImage: string }>(({ backgroundImage }) => ({
  width: '42px',
  height: '42px',
  flexShrink: 0,
  backgroundImage: `url(${backgroundImage})`,
  backgroundPosition: 'center',
  backgroundSize: 'cover',
  backgroundRepeat: 'no-repeat',
  borderRadius: 4,
}));

const DocumentTableBody: FC<IDocumentTableBodyProps> = ({
  'data-testid': dataTestId = '',
  rowsPerPage,
  currentPage,
  documents,
  selectedIds,
  actions,
  showPagination,
  disableSelection,
  headerColumns,
  onSelect,
  handleClickDocument,
}) => {
  const handleSelectRow = (event: MouseEvent, rowDocument: IBaseDocument) => {
    event.stopPropagation();
    onSelect(rowDocument.id);
  };

  const filteredDocuments = showPagination
    ? documents.slice(currentPage * rowsPerPage, currentPage * rowsPerPage + rowsPerPage)
    : documents;

  return (
    <StyledTableBody data-testid={dataTestId}>
      {filteredDocuments.map(doc => {
        const { id, usageCount, issuedDate, expiryDate } = doc;
        const isItemSelected = includes(selectedIds, id);
        const labelId = `enhanced-table-checkbox-${id}`;

        return (
          <StyledTableRow
            hover
            role="checkbox"
            data-testid="document-table-item"
            aria-checked={Booleanish(isItemSelected)}
            key={id}
            is-selected={Booleanish(isItemSelected)}
            onClick={(event: MouseEvent) =>
              handleClickDocument ? handleClickDocument(doc) : handleSelectRow(event, doc)
            }
          >
            <TableCell padding="checkbox">
              <Checkbox
                onClick={(event: MouseEvent<HTMLButtonElement>) => handleSelectRow(event, doc)}
                checked={isItemSelected}
                inputProps={{
                  'aria-labelledby': labelId,
                }}
                disabled={disableSelection}
                data-testid="document-table-checkbox"
              />
            </TableCell>
            <>
              {headerColumns.map(({ id: columnId }) => {
                switch (columnId) {
                  case EDocumentsTableColumnKeys.TITLE: {
                    return (
                      <TableCell key={columnId} id={labelId} scope="row">
                        <DocumentIntroTitle document={doc} />
                      </TableCell>
                    );
                  }
                  case EDocumentsTableColumnKeys.OWNER: {
                    return (
                      <TableCell key={columnId} id={labelId} scope="row">
                        <Box display="flex" alignItems="center">
                          <StyledImage
                            backgroundImage={doc.ownedBy?.logo?.url || PartnerPlaceholderImage}
                          />
                          <Box ml={1} />
                          <ThemeTypography variant="BODY_MEDIUM" autoOverflow>
                            {doc.ownedBy?.name}
                          </ThemeTypography>
                        </Box>
                      </TableCell>
                    );
                  }
                  case EDocumentsTableColumnKeys.CREATOR: {
                    return (
                      <TableCell key={columnId} id={labelId} scope="row">
                        <Box display="flex" alignItems="center">
                          <StyledImage
                            backgroundImage={doc.createdBy?.logo?.url || PartnerPlaceholderImage}
                          />
                          <Box ml={1} />
                          <ThemeTypography variant="BODY_MEDIUM" autoOverflow>
                            {doc.createdBy.name}
                          </ThemeTypography>
                        </Box>
                      </TableCell>
                    );
                  }
                  case EDocumentsTableColumnKeys.USAGE: {
                    return (
                      <TableCell key={columnId} id={labelId} scope="row">
                        <Tooltip
                          title={
                            usageCount
                              ? `The document is used in ${usageCount} impact claim${
                                  usageCount > 1 ? 's' : ''
                                }`
                              : 'No usage in impact claims'
                          }
                        >
                          <Box display="flex" alignItems="center">
                            <UsageIcon size={16} />
                            {usageCount}
                          </Box>
                        </Tooltip>
                      </TableCell>
                    );
                  }
                  case EDocumentsTableColumnKeys.ISSUE_DATE: {
                    return (
                      <TableCell key={columnId} id={labelId} scope="row">
                        <ThemeTypography variant="BODY_MEDIUM" autoOverflow>
                          {issuedDate && <Moment format="LL">{issuedDate}</Moment>}
                        </ThemeTypography>
                      </TableCell>
                    );
                  }
                  case EDocumentsTableColumnKeys.EXPIRY_DATE: {
                    return (
                      <TableCell key={columnId} id={labelId} scope="row">
                        {expiryDate &&
                          (moment(expiryDate).isBefore(moment(new Date())) ? (
                            <Tooltip title="The document has expired">
                              <ExpiredDate>
                                <Moment format="LL">{expiryDate}</Moment>
                              </ExpiredDate>
                            </Tooltip>
                          ) : (
                            <ThemeTypography variant="BODY_MEDIUM" autoOverflow>
                              <Moment format="LL">{expiryDate}</Moment>
                            </ThemeTypography>
                          ))}
                      </TableCell>
                    );
                  }
                  case EDocumentsTableColumnKeys.ACTIONS: {
                    return (
                      <TableCell key={columnId} id={labelId} scope="row" width={150}>
                        {actions && (
                          <Box display="flex" alignItems="center" justifyContent="center">
                            {actions.map((action, index) => {
                              const disabled =
                                action.disableIfDocumentUsed && (!doc || !!doc.usageCount);

                              return (
                                <Tooltip
                                  key={index}
                                  title={
                                    disabled && action.disabledTooltip
                                      ? action.disabledTooltip
                                      : action.tooltip
                                  }
                                >
                                  {/* Wrapper to show the tooltip even when disabled */}
                                  <Box component="span">
                                    <StyledIconButton
                                      disabled={disabled}
                                      data-testid={action.dataTestId}
                                      onClick={event => {
                                        event.stopPropagation();
                                        action.onClick(doc);
                                      }}
                                    >
                                      {action.icon}
                                    </StyledIconButton>
                                  </Box>
                                </Tooltip>
                              );
                            })}
                          </Box>
                        )}
                      </TableCell>
                    );
                  }
                  default:
                    return null;
                }
              })}
            </>
          </StyledTableRow>
        );
      })}
    </StyledTableBody>
  );
};

export default DocumentTableBody;
